import { useEffect, useRef, useState } from "react";
import { bucketUrlPrefix } from "common/config";
import {
  AccessibilityCategory,
  AccessibilityTag,
  ReviewPhoto,
  VenueDetails,
} from "common/types";
import AnalyticsService from "@/services/analytics";
import { useI18N } from "common/hooks";
import { formatFullDateAsShort } from "common/utils/general";
import { ChevronRight } from "lucide-react";

interface LatestReviewDetailUpdatedDate {
  latest_review_posted_at: string;
  latest_review_submitted_by: number;
  latest_review_id: number;
}

type AccessibilitySubItemProps = {
  qtagsAll: string;
  qtags: AccessibilityTag[];
  sections: any;
  reviewPhotos: ReviewPhoto[];
  fullSize?: boolean;
  venueId: string;
  venueDetails: VenueDetails;
  onFlag: (mainTag: any) => void;
  lastUpdated: string;
  categories: AccessibilityCategory[];
  cid?: string;
  tid?: string;
  onShowDetailView?: (initialSpaceId: string) => void;
  latestReviewDetailUpdatedDate?: LatestReviewDetailUpdatedDate;
};

function AccessibilitySubItem({
  qtags,
  qtagsAll,
  venueDetails,
  reviewPhotos,
  categories,
  cid,
  tid,
  onShowDetailView,
  latestReviewDetailUpdatedDate,
}: AccessibilitySubItemProps) {
  const i18n = useI18N();
  const hasAutoOpened = useRef(false);
  const photoScrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Calculate the initial space id for the detail view based on current qtagsAll
  const getInitialSpaceId = (): string => {
    for (const category of categories) {
      const tagIndex = category.tags.findIndex(
        (tag) => tag.qtagsAll === qtagsAll,
      );
      if (tagIndex !== -1) {
        return `${category.catId}-${tagIndex}`;
      }
    }
    return "";
  };

  // Auto-open detail view if cid and tid are passed
  useEffect(() => {
    if (cid && tid && onShowDetailView && !hasAutoOpened.current) {
      // Find the index from qtags where category_id = cid and tag_id = tid
      const categoryTags = categories.find(
        (category) => category.catId === parseInt(cid),
      )?.tags;
      let tagIndex = -1;
      if (categoryTags) {
        tagIndex = categoryTags.findIndex((tag) =>
          tag.qtags.find((qtag) => qtag.tagId === parseInt(tid)),
        );
      }

      // Only proceed if we found a valid tag index
      if (tagIndex === -1) {
        return;
      }

      const initialSpaceId = `${cid}-${tagIndex}`;
      hasAutoOpened.current = true;
      AnalyticsService.trackGAEventHandler("accessibilityItemOpen");
      AnalyticsService.trackMx("accessibilityItemOpen", {
        VenueId: venueDetails.business.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
        MainTag: qtagsAll.split(",")[0],
      });
      onShowDetailView(initialSpaceId);
    }
  }, [cid, tid, onShowDetailView, venueDetails, qtagsAll, categories]);

  const tags = qtagsAll.split(",");
  const mainTag = tags[0];
  const photos = reviewPhotos;

  // Get all tag names (excluding the main tag for display in bullet list)
  const allTagNames = qtags
    .map((qtag) => qtag.tagName)
    .filter((name) => name !== mainTag);

  const totalTagsCount = qtags.length;

  const handleViewAllClick = () => {
    AnalyticsService.trackGAEventHandler("accessibilityItemOpen");
    AnalyticsService.trackMx("accessibilityItemOpen", {
      VenueId: venueDetails.business.id,
      VenueBrandId: venueDetails.brandId,
      VenueBrandName: venueDetails.brandName,
      VenueAddress: venueDetails.address,
      VenueName: venueDetails.name,
      VenueType: venueDetails.businessType,
      MainTag: mainTag,
    });
    if (onShowDetailView) {
      onShowDetailView(getInitialSpaceId());
    }
  };

  // Carousel handlers
  const handlePhotoScrollLeft = () => {
    AnalyticsService.trackGAEventHandler("venueDetailAccessibilityPhotoScrollLeft");
    AnalyticsService.trackMx("venueDetailAccessibilityPhotoScrollLeft", {
      VenueId: venueDetails?.business?.id,
      VenueName: venueDetails?.name,
    });
    
    if (photoScrollContainerRef.current && photos.length > 0) {
      const container = photoScrollContainerRef.current;
      const scrollAmount = container.clientWidth;
      
      setCurrentPhotoIndex((prev) => {
        const newIndex = prev > 0 ? prev - 1 : photos.length - 1;
        const isLooping = prev === 0;
        
        // Instant scroll when looping, smooth scroll for normal navigation
        container.scrollTo({ 
          left: newIndex * scrollAmount, 
          behavior: isLooping ? "auto" : "smooth" 
        });
        return newIndex;
      });
    }
  };

  const handlePhotoScrollRight = () => {
    AnalyticsService.trackGAEventHandler("venueDetailAccessibilityPhotoScrollRight");
    AnalyticsService.trackMx("venueDetailAccessibilityPhotoScrollRight", {
      VenueId: venueDetails?.business?.id,
      VenueName: venueDetails?.name,
    });
    
    if (photoScrollContainerRef.current && photos.length > 0) {
      const container = photoScrollContainerRef.current;
      const scrollAmount = container.clientWidth;
      
      setCurrentPhotoIndex((prev) => {
        const newIndex = prev < photos.length - 1 ? prev + 1 : 0;
        const isLooping = prev === photos.length - 1;
        
        // Instant scroll when looping, smooth scroll for normal navigation
        container.scrollTo({ 
          left: newIndex * scrollAmount, 
          behavior: isLooping ? "auto" : "smooth" 
        });
        return newIndex;
      });
    }
  };

  // Get the thumbnail image (fallback for when no photos available)
  const fallbackThumbnailSrc =
    qtags.length > 0 && qtags[0].tag_image
      ? bucketUrlPrefix + qtags[0].tag_image
      : null;

  return (
    <>
      {/* Space Card */}
      <article className="flex flex-col sm:flex-row bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-xl overflow-hidden">
        {mainTag === "No bathroom onsite" ? (
          <>
            <div className="w-full sm:w-[160px] md:w-[180px] lg:w-[200px] p-6 flex-shrink-0 relative">
              {/* Content Section - Centered */}
              <div className="flex items-center justify-center h-full">
                <h3 className="label-l-bold text-fg-neutral-strong text-center">
                  {mainTag}
                </h3>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Image Section */}
            <div className="w-full sm:w-[160px] md:w-[180px] lg:w-[200px] h-42 sm:h-32 md:h-[166px] flex-shrink-0 relative">
              {photos && photos.length > 0 ? (
                <>
                  {/* Carousel Navigation Buttons - Only show if more than 1 photo */}
                  {photos.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={handlePhotoScrollLeft}
                        className="flex absolute left-2 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-8 h-8 bg-container-brand-minimal border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover transition-all focus-blue shadow-md hover:shadow-lg"
                        aria-label={i18n.get("previousPhoto")}
                      >
                        <ChevronRight
                          size={16}
                          className="rotate-180 text-fg-neutral-strong"
                        />
                      </button>

                      <button
                        type="button"
                        onClick={handlePhotoScrollRight}
                        className="flex absolute right-2 top-1/2 -translate-y-1/2 z-10 items-center justify-center w-8 h-8 bg-container-brand-minimal border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover transition-all focus-blue shadow-md hover:shadow-lg"
                        aria-label={i18n.get("nextPhoto")}
                      >
                        <ChevronRight size={16} className="text-fg-neutral-strong" />
                      </button>
                    </>
                  )}

                  {/* Photo Carousel */}
                  <div className="overflow-hidden w-full h-full rounded-tl-xl rounded-bl-xl sm:rounded-tr-none sm:rounded-br-none rounded-tr-xl rounded-br-xl">
                    <div
                      ref={photoScrollContainerRef}
                      className="flex w-full h-full overflow-x-auto overflow-y-hidden scrollbar-hide snap-x snap-mandatory"
                      role="list"
                      aria-label={`${mainTag} photos`}
                    >
                      {photos.map((photo, index) => (
                        <div
                          key={`photo-${index}`}
                          className="flex-shrink-0 w-full h-full snap-center"
                          role="listitem"
                        >
                          <button
                            onClick={handleViewAllClick}
                            className="w-full h-full p-0 block focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 cursor-pointer group relative overflow-hidden"
                            aria-label={`View ${mainTag} details - Photo ${index + 1} of ${photos.length}${photo.alt_text ? `: ${photo.alt_text}` : ""}`}
                            tabIndex={index === currentPhotoIndex ? 0 : -1}
                          >
                            <img
                              src={bucketUrlPrefix + photo.path}
                              alt=""
                              aria-hidden="true"
                              className="w-full h-full object-cover block"
                            />

                            {/* Hover gradient overlay */}
                            <div className="absolute inset-0 bg-[#070708] opacity-0 group-hover:opacity-[0.12] group-active:opacity-[0.12] transition-opacity duration-200 pointer-events-none" aria-hidden="true" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Fixed position indicator - always visible, never scrolls */}
                  {photos.length > 1 && (
                    <>
                      <div
                        className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black bg-opacity-70 text-white body-s-regular px-2 py-1 rounded z-20 pointer-events-none"
                        aria-hidden="true"
                      >
                        {currentPhotoIndex + 1}/{photos.length}
                      </div>
                      {/* Screen-reader live region announces slide changes */}
                      <div
                        aria-live="polite"
                        aria-atomic="true"
                        className="sr-only"
                      >
                        {`Photo ${currentPhotoIndex + 1} of ${photos.length}${photos[currentPhotoIndex]?.alt_text ? `: ${photos[currentPhotoIndex].alt_text}` : ""}`}
                      </div>
                    </>
                  )}
                </>
              ) : (
                /* Fallback when no photos available */
                <button
                  onClick={handleViewAllClick}
                  className="w-full h-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-tl-xl rounded-bl-xl sm:rounded-tr-none sm:rounded-br-none rounded-tr-xl rounded-br-xl cursor-pointer group relative overflow-hidden"
                  aria-label={`View ${mainTag} details`}
                >
                  {/* Background layer - Checkerboard pattern */}
                  <div
                    className="absolute inset-0 w-full h-full"
                    style={{
                      backgroundImage:
                        "url(/images/No_Data_Placeholder_Images_Background.png)",
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                    }}
                  />
                  {/* Foreground layer - Sociability icon centered */}
                  <img
                    src="/images/No_Data_Placeholder_Images_Foreground.png"
                    alt=""
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[200px] object-contain z-10"
                    aria-hidden="true"
                  />

                  {/* Hover gradient overlay */}
                  <div className="absolute inset-0 bg-[#070708] opacity-0 group-hover:opacity-[0.12] group-active:opacity-[0.12] transition-opacity duration-200 pointer-events-none rounded" />
                </button>
              )}
            </div>

            {/* Content Section */}
            <button
              type="button"
              onClick={handleViewAllClick}
              className="flex-1 flex flex-col gap-3 p-3 overflow-hidden text-left focus-blue group hover:bg-bg-brand-ghost-hover active:bg-bg-brand-ghost-hover transition-colors duration-200"
              aria-label={`View all ${totalTagsCount} accessibility tags for ${mainTag}`}
            >
              {/* Title & Meta */}
              <div className="px-2">
                <h3 className="label-l-bold text-fg-neutral-strong">
                  {mainTag}
                </h3>

                {latestReviewDetailUpdatedDate?.latest_review_posted_at && (
                  <p className="label-s-medium text-fg-neutral-subtle">
                    {i18n.get("lastUpdated")}: {formatFullDateAsShort(latestReviewDetailUpdatedDate.latest_review_posted_at)}
                  </p>
                )}

              </div>

              {/* Tags List - Fixed 2-line height */}
              <div className="px-2">
                <div className="h-12 overflow-hidden">
                  <p className="label-m-medium text-fg-neutral-strong line-clamp-2">
                    {(() => {
                      if (!allTagNames || allTagNames.length === 0) {
                        return "• No additional accessibility information available";
                      }

                      // Show first 4 tags to ensure ellipsis fits
                      const tagsToShow = allTagNames.slice(0, 6);
                      const hasMore = allTagNames.length > 6;
                      const tagText = tagsToShow.map((tag) => `• ${tag}`).join(" ");

                      return hasMore ? `${tagText} ...` : tagText;
                    })()}
                  </p>
                </div>
              </div>

              {/* Divider */}
              <hr
                className="border-border-token-brand-minimal"
                aria-hidden="true"
              />

              {/* CTA Visual */}
              <div className="flex items-center gap-2 px-2" aria-hidden="true">
                <span className="label-m-medium text-fg-brand-subtle group-hover:text-fg-brand-strong group-active:text-fg-brand-strong border-b border-transparent group-hover:border-border-token-brand-subtle group-active:border-border-token-brand-strong transition-all duration-200">
                  {i18n.get("viewAll") || "View all"} {totalTagsCount}{" "}
                  {i18n.get("accessibilityTags") || "accessibility tags"}
                </span>
                <svg
                  className="w-5 h-5 transition-transform group-hover:translate-x-0.5 text-fg-brand-subtle group-hover:text-fg-brand-strong group-active:text-fg-brand-strong"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
                </svg>
              </div>
            </button>
          </>
        )}
      </article>
    </>
  );
}

export default AccessibilitySubItem;
